import { Menu01Icon } from '@hugeicons/react'
import clsx from 'clsx'
import _ from 'lodash'

import { Avatar } from '@/shared/ui/Avatar'
import { useTopbarSkin, useTopbarVariant } from '@/shared/ui/Layout/chrome/TopBar'

import { useAppMenu } from './AppMenuContext'

type AppMenuProfileUser = {
  name?: string
  email?: string
  avatar?: { file?: { url?: string } }
} | null

type AppMenuProfileTriggerProps = {
  user: AppMenuProfileUser
  hasAvatar: boolean
  avatar: unknown
}

function ProfileAvatarThumbnail({
  user,
  hasAvatar,
  avatar
}: Pick<AppMenuProfileTriggerProps, 'user' | 'hasAvatar' | 'avatar'>) {
  // A chapa branca do trigger é a mesma nas duas skins, então o thumbnail não
  // precisa saber do tema — quem se adapta é o botão em volta.
  const mediaClass =
    'h-full w-full overflow-hidden rounded-[6px] object-cover object-center'

  if (hasAvatar) {
    return (
      <Avatar config={avatar} showFullBody={false} className={mediaClass} />
    )
  }

  const photoUrl = _.get(user, 'avatar.file.url') || '/placeholders/user.svg'

  return (
    <img
      src={photoUrl}
      alt={_.get(user, 'name', '')}
      className={mediaClass}
    />
  )
}

export function AppMenuProfileTrigger({
  user,
  hasAvatar,
  avatar
}: AppMenuProfileTriggerProps) {
  const { open } = useAppMenu()
  const variant = useTopbarVariant()
  const skin = useTopbarSkin()

  const isImmersive = variant === 'immersive'
  // `immersive` sempre resolve skin `dark` (ver `resolveTopbarSkin`). O widget
  // translúcido e o ícone branco valem para as duas skins escuras — mesmo
  // tratamento do `Wallet`. Exclusivo do `immersive` é só o brilho âmbar.
  const isDark = skin === 'dark'

  return (
    <button
      type="button"
      className={clsx(
        'gami-topbar-widget flex h-12 cursor-pointer items-center gap-1 rounded-lg border-0 p-1',
        isDark && 'gami-topbar-widget--immersive'
      )}
      onClick={() => open({ source: 'profile' })}
      aria-label="Abrir menu do usuário"
    >
      <div
        className={clsx(
          'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white p-0.5 ring-1 ring-black/5',
          isImmersive ? 'shadow-glow-gold' : 'shadow-glow-brand'
        )}
      >
        <ProfileAvatarThumbnail
          user={user}
          hasAvatar={hasAvatar}
          avatar={avatar}
        />
      </div>

      <span
        aria-hidden
        className="flex h-10 w-10 shrink-0 items-center justify-center"
      >
        <Menu01Icon
          variant="solid"
          size={20}
          className={clsx(isDark ? 'text-white' : 'text-brand')}
        />
      </span>
    </button>
  )
}
